import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer, LayoutList, Watch } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PlayCanvas } from "@/components/field/PlayCanvas";
import { catLabel } from "@/components/PlayFilters";
import { jobFor, phasesSorted } from "@/lib/playEngine";

const KEY = "Solid arrow = run/route · T end = block · Dotted = motion · Dashed blue = pass · Yellow dots = snap/handoff/pitch · Circle = offense · Diamond = defense";

function PrintCard({ play, team, num }) {
  const us = play.category === "defense" ? "defense" : "offense";
  const players = play.players.filter((p) => p.side === us);
  return (
    <section className="print-page bg-white text-[#0B1528] rounded-xl p-5 sm:p-6 mb-6" data-testid={`print-card-${play.id}`}>
      <header className="flex items-start justify-between gap-3 border-b-2 border-[#0B1528] pb-2 mb-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#CC5200]">{team.name}{team.age_group && ` · ${team.age_group}`} · {catLabel(play.category)}{play.formation && ` · ${play.formation}`}</p>
          <h2 className="font-display text-3xl font-extrabold uppercase leading-tight">{play.name}</h2>
        </div>
        <span className="font-display text-4xl font-extrabold text-[#CC5200]">#{num}</span>
      </header>
      <div className="grid sm:grid-cols-[1.5fr_1fr] print:grid-cols-[1.5fr_1fr] gap-4">
        <PlayCanvas play={play} team={team} />
        <div className="text-sm space-y-2">
          <p className="font-bold uppercase text-xs tracking-wider">Phases</p>
          <ol className="list-decimal pl-5">{phasesSorted(play).map((ph) => <li key={ph.id}>{ph.name} <span className="text-slate-500">({ph.time}s)</span></li>)}</ol>
          <p className="text-[11px] text-slate-600">{KEY}</p>
          {play.is_example && <p className="text-[11px] font-bold">Example play: adapt it to your league and team.</p>}
        </div>
      </div>
      <table className="w-full text-[12px] mt-4 border-collapse">
        <thead><tr className="bg-[#0B1528] text-white text-left">{["Pos", "Line up", "When to go", "Where to move", "Coach's tip"].map((h) => <th key={h} className="p-1.5">{h}</th>)}</tr></thead>
        <tbody>
          {players.map((p) => { const j = jobFor(play, p); return (
            <tr key={p.id} className="border-b border-slate-300 align-top" style={{ breakInside: "avoid" }}>
              <td className="p-1.5 font-extrabold whitespace-nowrap">{p.label}{p.nickname && <span className="block font-normal text-slate-500">{p.nickname}</span>}</td>
              <td className="p-1.5">{j.lineup}</td><td className="p-1.5">{j.when}</td><td className="p-1.5">{j.where}</td><td className="p-1.5">{j.instruction}</td>
            </tr>
          ); })}
        </tbody>
      </table>
    </section>
  );
}

function WristSheet({ plays, team }) {
  return (
    <section className="bg-white text-[#0B1528] rounded-xl p-4 wrist-grid grid grid-cols-2 sm:grid-cols-3 gap-0" data-testid="print-wristband-sheet">
      {plays.map((p, i) => (
        <div key={p.id} className="wrist-cell border border-dashed border-slate-400 p-1.5" data-testid={`wrist-cell-${p.id}`}>
          <p className="text-[11px] font-extrabold uppercase leading-tight truncate"><span className="text-[#CC5200]">#{i + 1}</span> {p.name}</p>
          <PlayCanvas play={p} team={team} />
        </div>
      ))}
    </section>
  );
}

export default function PrintPlays() {
  const { teamId } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [team, setTeam] = useState(null);
  const [plays, setPlays] = useState([]);
  const [chosen, setChosen] = useState(new Set());
  const [mode, setMode] = useState(sp.get("mode") === "wrist" ? "wrist" : "card");

  useEffect(() => {
    Promise.all([api.get(`/teams/${teamId}`), api.get(`/teams/${teamId}/plays`)]).then(([t, p]) => {
      setTeam(t.data); setPlays(p.data);
      const ids = sp.get("plays")?.split(",").filter(Boolean);
      setChosen(new Set(ids?.length ? ids : p.data.map((x) => x.id)));
    }).catch((e) => { toast.error(errMsg(e)); nav("/teams"); });
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useMemo(() => plays.filter((p) => chosen.has(p.id)), [plays, chosen]);
  const toggle = (id) => setChosen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  if (!team) return <div className="min-h-screen grid place-items-center bg-[#070D18] text-slate-400" data-testid="print-loading">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#070D18] text-white print:bg-white">
      <div className="no-print sticky top-0 z-40 bg-[#0B1528] border-b border-[#1F3358]">
        <div className="max-w-5xl mx-auto px-3 py-2 flex flex-wrap items-center gap-2">
          <Link to={`/teams/${teamId}/playbook`} className="btn btn-ghost px-3" data-testid="print-back-playbook"><ArrowLeft size={18} />Playbook</Link>
          <div className="flex rounded-xl border border-[#1F3358] overflow-hidden" role="group" aria-label="Print layout">
            <button className={`min-h-[44px] px-3 text-sm font-bold flex items-center gap-1.5 ${mode === "card" ? "bg-[#FF6B00]" : "text-slate-300"}`} onClick={() => setMode("card")} data-testid="print-mode-card"><LayoutList size={16} />Play cards</button>
            <button className={`min-h-[44px] px-3 text-sm font-bold flex items-center gap-1.5 ${mode === "wrist" ? "bg-[#FF6B00]" : "text-slate-300"}`} onClick={() => setMode("wrist")} data-testid="print-mode-wrist"><Watch size={16} />Wristband sheet</button>
          </div>
          <button className="btn btn-primary ml-auto" onClick={() => window.print()} disabled={!list.length} data-testid="btn-print-now"><Printer size={18} />Print {list.length} play{list.length === 1 ? "" : "s"}</button>
        </div>
      </div>
      <div className="no-print max-w-5xl mx-auto px-3 pt-4">
        <p className="label">Plays to print</p>
        <div className="flex flex-wrap gap-2" data-testid="print-play-picker">
          {plays.map((p) => <button key={p.id} className={`chip ${chosen.has(p.id) ? "chip-on" : ""}`} aria-pressed={chosen.has(p.id)} onClick={() => toggle(p.id)} data-testid={`print-pick-${p.id}`}>{p.name}</button>)}
        </div>
        <p className="text-xs text-slate-500 mt-2">{mode === "card" ? "One play per page with each player's job." : "Small numbered diagrams. Cut along the dashed lines for wristbands or a call sheet."} Tip: turn on "Background graphics" in the print dialog to keep the field colors.</p>
      </div>
      <main className="max-w-5xl mx-auto p-3 print:p-0 print:max-w-none">
        {!list.length ? <p className="text-slate-400 p-6 text-center" data-testid="print-empty">Pick at least one play.</p> :
          mode === "card" ? list.map((p, i) => <PrintCard key={p.id} play={p} team={team} num={i + 1} />) : <WristSheet plays={list} team={team} />}
      </main>
    </div>
  );
}
